import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { booksService } from '../../services/api';
import { Book, BooksQueryParams, CreateBookData, UpdateBookData } from '../../types';

interface BooksState {
  books: Book[];
  deletedBooks: Book[];
  currentBook: Book | null;
  total: number;
  page: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
}

const initialState: BooksState = {
  books: [],
  deletedBooks: [],
  currentBook: null,
  total: 0,
  page: 1,
  totalPages: 1,
  isLoading: false,
  error: null,
};

export const fetchBooks = createAsyncThunk(
  'books/fetchBooks',
  async (params: BooksQueryParams | undefined, { rejectWithValue }) => {
    try {
      return await booksService.getBooks(params);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch books');
    }
  }
);

export const fetchBookById = createAsyncThunk(
  'books/fetchBookById',
  async (id: string, { rejectWithValue }) => {
    try {
      return await booksService.getBookById(id);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch book');
    }
  }
);

export const fetchDeletedBooks = createAsyncThunk(
  'books/fetchDeletedBooks',
  async (_, { rejectWithValue }) => {
    try {
      return await booksService.getDeletedBooks();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch deleted books');
    }
  }
);

export const createBook = createAsyncThunk(
  'books/createBook',
  async (
    { data, coverImage, bookFile }: { data: CreateBookData; coverImage?: File; bookFile?: File },
    { rejectWithValue }
  ) => {
    try {
      return await booksService.createBook(data, coverImage, bookFile);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create book');
    }
  }
);

export const updateBook = createAsyncThunk(
  'books/updateBook',
  async (
    { id, data, coverImage, bookFile }: { id: string; data: UpdateBookData; coverImage?: File; bookFile?: File },
    { rejectWithValue }
  ) => {
    try {
      return await booksService.updateBook(id, data, coverImage, bookFile);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update book');
    }
  }
);

export const deleteBook = createAsyncThunk(
  'books/deleteBook',
  async (id: string, { rejectWithValue }) => {
    try {
      await booksService.deleteBook(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete book');
    }
  }
);

export const hardDeleteBook = createAsyncThunk(
  'books/hardDeleteBook',
  async (id: string, { rejectWithValue }) => {
    try {
      await booksService.hardDeleteBook(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to permanently delete book');
    }
  }
);

export const restoreBook = createAsyncThunk(
  'books/restoreBook',
  async (id: string, { rejectWithValue }) => {
    try {
      return await booksService.restoreBook(id);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to restore book');
    }
  }
);

const booksSlice = createSlice({
  name: 'books',
  initialState,
  reducers: {
    clearCurrentBook(state) {
      state.currentBook = null;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Books
      .addCase(fetchBooks.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBooks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.books = action.payload.books;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchBooks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Book by ID
      .addCase(fetchBookById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBookById.fulfilled, (state, action: PayloadAction<Book>) => {
        state.isLoading = false;
        state.currentBook = action.payload;
      })
      .addCase(fetchBookById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Deleted Books
      .addCase(fetchDeletedBooks.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDeletedBooks.fulfilled, (state, action: PayloadAction<Book[]>) => {
        state.isLoading = false;
        state.deletedBooks = action.payload;
      })
      .addCase(fetchDeletedBooks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create Book
      .addCase(createBook.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createBook.fulfilled, (state, action: PayloadAction<Book>) => {
        state.isLoading = false;
        state.books.unshift(action.payload);
        state.total += 1;
      })
      .addCase(createBook.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update Book
      .addCase(updateBook.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateBook.fulfilled, (state, action: PayloadAction<Book>) => {
        state.isLoading = false;
        const index = state.books.findIndex((book) => book._id === action.payload._id);
        if (index !== -1) {
          state.books[index] = action.payload;
        }
        state.currentBook = action.payload;
      })
      .addCase(updateBook.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Delete Book
      .addCase(deleteBook.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteBook.fulfilled, (state, action: PayloadAction<string>) => {
        state.isLoading = false;
        state.books = state.books.filter((book) => book._id !== action.payload);
        state.total -= 1;
      })
      .addCase(deleteBook.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Hard Delete Book
      .addCase(hardDeleteBook.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(hardDeleteBook.fulfilled, (state, action: PayloadAction<string>) => {
        state.isLoading = false;
        state.deletedBooks = state.deletedBooks.filter((book) => book._id !== action.payload);
      })
      .addCase(hardDeleteBook.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Restore Book
      .addCase(restoreBook.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(restoreBook.fulfilled, (state, action: PayloadAction<Book>) => {
        state.isLoading = false;
        state.deletedBooks = state.deletedBooks.filter((book) => book._id !== action.payload._id);
        state.books.unshift(action.payload);
      })
      .addCase(restoreBook.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearCurrentBook, clearError } = booksSlice.actions;
export default booksSlice.reducer;
